import { useEffect, useState } from "react";
import { Entity } from "@backstage/catalog-model";
import { identityApiRef, useApi } from "@backstage/core-plugin-api";
import { DefaultEntityFilters, EntityFilter, EntityKindPicker, EntityNamespacePicker, EntityOwnerPicker, EntityTypePicker, useEntityList, UserListPicker, EntityOwnerFilter } from "@backstage/plugin-catalog-react";

type GroupsFilters = DefaultEntityFilters & {
  groupOwnership?: GroupOwnershipEntityFilter;
};

class UniversalOwnerEntityFilter implements EntityOwnerFilter {
  readonly values: string[];

  constructor(selectedOwners: string[]) {
    this.values = selectedOwners;
  }

  filterEntity(entity: Entity): boolean {
    const isUniversalTemplate =
      entity.metadata?.annotations?.['xvia.backstage.io/universal-template'] === 'true';

    if (isUniversalTemplate) {
      return true;
    }

    if (!this.values || this.values.length === 0) {
      return true;
    }

    const entityOwner = entity.spec?.owner?.toString() ?? '';

    return this.values.some(selectedOwner => {
      const normalizedEntityOwner = entityOwner.toLowerCase();
      const normalizedSelected = selectedOwner.toLowerCase();

      return (
        normalizedEntityOwner === normalizedSelected ||
        normalizedEntityOwner.includes(normalizedSelected) ||
        normalizedEntityOwner.endsWith(`/${normalizedSelected}`)
      );
    });
  }

  getCatalogFilters(): Record<string, string | string[]> {
    return this.values.length ? { owners: this.values } : {};
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

class GroupOwnershipEntityFilter implements EntityFilter {
  constructor(private readonly allowedGroups: string[]) {}

  filterEntity(entity: Entity): boolean {
    const isUniversalTemplate =
      entity.metadata?.annotations?.['xvia.backstage.io/universal-template'] === 'true';

    if (isUniversalTemplate) {
      return true;
    }

    const kind = entity.kind?.toLowerCase();

    if (kind === 'user') {
      const userGroups = (entity.spec?.memberOf as string[]) ?? [];

      return userGroups.some(userGroup =>
        this.allowedGroups.some(allowedGroup =>
          allowedGroup.toLowerCase().includes(userGroup.toLowerCase())
        )
      );
    }

    if (kind === 'group') {
      const groupName = entity.metadata?.name?.toLowerCase() ?? '';

      return this.allowedGroups.some(allowedGroup =>
        allowedGroup.toLowerCase().includes(groupName)
      );
    }

    if (['system', 'location'].includes(kind)) {
      return true;
    }

    const owner = entity.spec?.owner?.toString().toLowerCase() ?? '';

    return this.allowedGroups.some(group =>
      owner.includes(group.split('/').pop()?.toLowerCase() ?? '')
    );
  }
}

export const GroupsPicker = () => {
  const [userOwnershipEntities, setUserOwnershipEntities] = useState<string[]>([]);
  const identityApi = useApi(identityApiRef);
  const { filters, updateFilters } = useEntityList<GroupsFilters>();

  useEffect(() => {
      identityApi.getBackstageIdentity().then(identity => {
        const ownershipEntities = identity.ownershipEntityRefs ?? [];

        setUserOwnershipEntities(ownershipEntities);
      });
    }, [identityApi]);

  useEffect(() => {
    updateFilters({
      groupOwnership: new GroupOwnershipEntityFilter(userOwnershipEntities),
    });
  }, [updateFilters, userOwnershipEntities]);

  useEffect(() => {
    const ownerFilter = filters.owners;

    if (ownerFilter && typeof ownerFilter !== 'function') {
      const selectedOwners = (ownerFilter as any).values || [];

      updateFilters({
        owners: new UniversalOwnerEntityFilter(selectedOwners),
      });
    }
  }, [filters.owners, updateFilters]);

  return (
    <>
      <EntityKindPicker />
      <EntityTypePicker />
      <UserListPicker
        availableFilters={['owned', 'starred']}
        initialFilter='all'
      />
      <EntityOwnerPicker />
      <EntityNamespacePicker />
    </>
  );
};